// Posições da mesa, em px do palco de 390x844 (em pé) ou 980x520 (deitado). Origem: protótipo.
// Os lugares são relativos: 0 é sempre você, embaixo; 1 a 5 seguem a ordem da mesa.
// No computador, a mesa usa as mesmas coordenadas, numa caixa que escala com a coluna do meio (PALCO_COMPUTADOR):
// a sua mão fica embaixo da mesa, o seu rosto à esquerda dela e o TRUCO à direita (tela 5.5 do canvas).
import type { Modo } from '../comum/Palco.tsx';

export type Ponto = readonly [number, number];

export function lugares(modo: Modo): Record<number, Ponto> {
  // Em pé, o seu rosto fica no seu lado da mesa, centralizado acima das cartas (canvas F06).
  const voce: Ponto = modo === 'computador' ? [-24, 650] : modo === 'deitado' ? [195, 572] : [195, 557];
  return { 0: voce, 1: [46, 470], 2: [46, 262], 3: [195, 150], 4: [344, 262], 5: [344, 470] };
}

// A caixa da coluna do meio no computador, nas coordenadas da mesa: do rosto à esquerda da mão até o TRUCO, e do
// rosto de cima até embaixo das cartas, com espaço para a votação abrir embaixo da mão sem cobrir as cartas.
export const PALCO_COMPUTADOR = { x: -92, y: 112, largura: 580, altura: 692 } as const;
export const TRUCO_COMPUTADOR = { x: 358, y: 600, largura: 124, altura: 104 } as const;
export const ATALHOS_COMPUTADOR = 734; // topo dos atalhos 1, 2, 3 e T, embaixo das cartas
export const JOGADA: Record<number, Ponto> = { 3: [169, 218], 2: [97, 274], 4: [241, 274], 1: [97, 385], 5: [241, 385], 0: [169, 441] };
export const ROTACAO: Record<number, number> = { 3: -4, 2: 6, 4: -6, 1: -5, 5: 5, 0: 2 };
export const MONTE: Ponto = [170, 331];
export const ONZE: Record<number, Ponto> = { 2: [92, 226], 4: [200, 226] };

// Cartas na mão: [esquerda, topo, giro], conforme quantas restam. Em pé, 80 x 112, abaixo do seu rosto
// (canvas F06); deitado, 96 x 134, à direita da mesa; no computador, 90 x 126, retas, embaixo da mesa.
const MAO: Record<number, [number, number, number][]> = {
  3: [[99, 602, -8], [155, 614, 0], [211, 620, 8]],
  2: [[126, 608, -5], [184, 612, 5]],
  1: [[155, 614, 0]],
};
const MAO_DEITADO: Record<number, [number, number, number][]> = {
  3: [[426, 232, -8], [487, 244, 0], [548, 250, 8]],
  2: [[456, 240, -5], [518, 242, 5]],
  1: [[487, 244, 0]],
};
const MAO_COMPUTADOR: Record<number, [number, number, number][]> = {
  3: [[51, 600, 0], [151, 600, 0], [251, 600, 0]],
  2: [[101, 600, 0], [201, 600, 0]],
  1: [[151, 600, 0]],
};
const MAOS: Record<Modo, Record<number, [number, number, number][]>> = { pe: MAO, deitado: MAO_DEITADO, computador: MAO_COMPUTADOR };
export const CARTA_NA_MAO: Record<Modo, readonly [number, number]> = { pe: [80, 112], deitado: [96, 134], computador: [90, 126] };
export function posicaoNaMao(n: number, i: number, modo: Modo): [number, number, number] {
  return MAOS[modo][n]?.[i] ?? MAOS[modo][1]![0]!;
}

// Lado da mesa de cada lugar, o monte de quem deu as cartas e para onde voam as suas cartas
// (protótipo: SIDE, deckPos e HAND_TO).
export const LADO: Record<number, Ponto> = { 0: [195, 560], 1: [79, 456], 2: [79, 276], 3: [195, 186], 4: [311, 276], 5: [311, 456] };
export function monteDe(r: number): Ponto {
  const c = LADO[r]!;
  return [Math.round(c[0] + (195 - c[0]) * 0.3 - 25), Math.round(c[1] + (366 - c[1]) * 0.3 - 35)];
}
export function maoAlvo(modo: Modo): Ponto {
  return modo === 'computador' ? [195, 661] : modo === 'deitado' ? [535, 300] : [195, 670];
}
// Centro de uma carta da sua mão e de uma carta jogada na mesa (52 x 73).
export function centroNaMao(n: number, i: number, modo: Modo): Ponto {
  const p = posicaoNaMao(n, i, modo), [w, h] = CARTA_NA_MAO[modo];
  return [p[0] + w / 2, p[1] + h / 2];
}
export function escalaDaMao(modo: Modo): number { return CARTA_NA_MAO[modo][0] / 52; }
